import React, { useEffect, useState } from 'react';
import { Row, Col, Popover } from 'antd';
import { getUserId, MESSAGE_TYPE, messageManager, translate } from '../../helper';
import TextBase from '../../common/text';
import { Colors } from '../../themes/Colors';
import LoginInput from '../../common/login-input';
import SelectCustom from '../../common/select-custom';
import ButtonGradient from '../../common/button-gradient';
import FormArea from '../../common/form-area';
import { SVGImageComponentReact } from '../../themes/Images';
import ImageZoom from '../../common/image-zoom';
import Modal from '@mui/material/Modal';
import ImageEditor from '../../common/ImageCropModal';
import DefaultAvatar from '../../assets/image/default-avatar.png';
import APIManager from '../../connection/ApiManager';
import { useSelector, useDispatch } from 'react-redux';
import { UserAction } from '../../redux/action';
import OTPModal from '../../component/OTPModal';
import { renderCountries, renderCountriesPhoneCode, renderStates } from '../../helper';
import CustomSpinner from '../../common/CustomSpinner';

const BusinessInformation = () => {
  const { userInfo } = useSelector((state) => ({ userInfo: state?.User?.userInfo }));
  const [isTeamMember, setIsTeamMember] = useState(true);
  const [loadingUpdateData, setLoadingUpdateData] = useState(false);
  const dispatch = useDispatch();

  const [logoImage, setLogoImage] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const [stateObj, setStateObj] = useState({
    dealerAvatar: null,
    previewDealerImage: '',
    dealerImageFile: null,
    vendorId: null,
    description: '',
    businessEmail: '',
    phoneNumber: '',
    phoneCode: '1',
    phoneNumberPrev: '',
    phoneCodePrev: '1',
    businessName: '',
    taxID: '',
    country: '',
    stateVal: '',
    city: '',
    zip: '',
    address: '',
    phoneVerified: false,
    showOTP: false,
    showOTPPopOver: false
  });

  const fetchData = async () => {
    setLoadingUpdateData(true);
    try {
      const id = getUserId(userInfo);
      if (!id) return;
      const resProfile = await APIManager.getInstance().getUserProfile(id);
      const resVendor = await APIManager.getInstance().getVendorProfile(id);
      const userData = resProfile?.data || {};
      const vendorData = resVendor?.data || {};
      setIsTeamMember(!!userData?.team_member);
      setStateObj((prev) => ({
        ...prev,
        description: vendorData?.description || '',
        businessEmail: vendorData?.business_email || '',
        phoneNumber: vendorData?.phone_number?.national_number || '',
        phoneCode: vendorData?.phone_number?.country_code || '1',
        phoneNumberPrev: vendorData?.phone_number?.national_number || '',
        phoneCodePrev: vendorData?.phone_number?.country_code || '1',
        businessName: vendorData?.business_name || '',
        taxID: vendorData?.tax_id || '',
        taxPercentage: vendorData?.tax_percentage || '',
        country: vendorData?.country || '',
        stateVal: vendorData?.state || '',
        city: vendorData?.city || '',
        zip: vendorData?.zip || '',
        address: vendorData?.address || '',
        phoneVerified: !!vendorData?.phone_verified,
        vendorId: vendorData?.id || null,
        dealerAvatar: vendorData?.logo || null
      }));
    } catch (e) {
      console.error('BusinessInformation.fetchData', e);
    } finally {
      setLoadingUpdateData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userInfo]);

  const onPhoneCodeSelect = (key, val) => {
    setStateObj((prev) => ({ ...prev, phoneCode: val }));
  };
  const onCountrySelect = (val) => {
    setStateObj((prev) => ({
      ...prev,
      country: val,
      stateVal: val !== 'US' && val !== 'CA' ? undefined : prev.stateVal
    }));
  };
  const onStateSelect = (val) => {
    setStateObj((prev) => ({ ...prev, stateVal: val }));
  };

  const onFileUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLogoImage(file);
    setIsEditorOpen(true);
  };

  const handleCloseEditor = () => {
    let input = document.getElementById('profile-upload');
    if (input) input.value = '';
    setLogoImage(null);
    setIsEditorOpen(false);
  };

  const handleCroppedImage2 = (img) => {
    try {
      let arr = img.split(',');
      let mime = arr[0].match(/:(.*?);/)[1];
      let bstr = atob(arr[1]);
      let n = bstr.length;
      let u8arr = new Uint8Array(n);
      while (n--) u8arr[n] = bstr.charCodeAt(n);
      const name = (logoImage && logoImage.name) || 'logo.png';
      let file = new File([u8arr], name, { type: mime });
      setIsEditorOpen(false);
      setStateObj((prev) => ({ ...prev, dealerImageFile: file, previewDealerImage: file }));
      handleCloseEditor();
    } catch (e) {
      console.error('handleCroppedImage', e);
    }
  };

  const closeModal = () => {
    setStateObj((prev) => ({ ...prev, showOTP: false }));
  };

  const getSMSCode = async () => {
    try {
      const { phoneNumberPrev, phoneNumber, phoneCode } = stateObj;
      if (phoneNumberPrev !== phoneNumber) {
        setStateObj((prev) => ({ ...prev, showOTPPopOver: true }));
        setTimeout(() => setStateObj((prev) => ({ ...prev, showOTPPopOver: false })), 3000);
        return;
      }

      await APIManager.getInstance().sendSMS({ phone_number: `+${phoneCode}${phoneNumber}` });
      setStateObj((prev) => ({ ...prev, showOTP: true }));
    } catch (err) {
      console.error('getSMSCode', err);
      setStateObj((prev) => ({ ...prev, showOTP: false }));
    }
  };

  const saveVendor = async () => {
    setLoadingUpdateData(true);
    try {
      const {
        dealerImageFile,
        vendorId,
        description,
        businessEmail,
        businessName,
        taxID,
        taxPercentage,
        country,
        city,
        zip,
        address,
        phoneNumber,
        phoneCode,
        stateVal,
        dealerAvatar
      } = stateObj;
      let anyUpdated = false;

      if (!isTeamMember) {
        if (dealerImageFile) {
          let formData = new FormData();
          formData.append('logo', dealerImageFile);
          const resLogo = await dispatch(UserAction.updateVendor(formData, vendorId));
          const logoUrl = resLogo?.data?.logo || resLogo?.data?.data?.logo;
          if (logoUrl) {
            setStateObj((prev) => ({ ...prev, dealerAvatar: logoUrl }));
            anyUpdated = true;
          }
        }

        let vendorFormData = {
          description,
          business_email: businessEmail,
          business_name: businessName,
          tax_id: taxID,
          tax_percentage: taxPercentage,
          country,
          city,
          zip,
          address,
          notification_thread: false
        };

        if (phoneNumber) {
          vendorFormData.phone_number = { national_number: phoneNumber, country_code: phoneCode };
        }
        if (stateVal) vendorFormData.state = stateVal;

        const resVendor = await dispatch(UserAction.updateVendor(vendorFormData, vendorId));
        const vendorData = resVendor?.data || resVendor?.data?.data || {};
        if (Object.keys(vendorData).length) {
          setStateObj((prev) => ({ ...prev, dealerAvatar: vendorData?.logo || dealerAvatar }));
          anyUpdated = true;
        }
      } else {
        messageManager({
          type: MESSAGE_TYPE.warning,
          messageShow: 'Team members cannot update vendor information.'
        });
      }

      if (anyUpdated) {
        messageManager({ type: MESSAGE_TYPE.success, messageShow: 'Updated successfully.' });
      }
    } catch (e) {
      console.error('saveVendor', e);
      messageManager({ type: MESSAGE_TYPE.error, messageShow: e?.message || 'Update failed.' });
    } finally {
      setLoadingUpdateData(false);
    }
  };
  const handleCancel = () => {
    fetchData();
  };

  return (
    <div style={{ position: 'relative', padding: 'calc(var(--sp-md2)*2)' }}>
      <div
        style={{
          display: "inline-grid",
          alignItems: "center",
          gridTemplateColumns: "auto 1fr",
          width: "100%",
          marginBottom: 'calc(var(--sp-md2)*1.88679)'
        }}
      >
        <span
          style={{
            fontSize: 'calc(var(--fs-xs)*1.698113)',
            width: 'fit-content',
            lineHeight: 'calc(var(--sp-lg)*1.273585)',
            fontWeight: '600',
            padding: 'calc(var(--sp-md2)*1) calc(var(--sp-md2)*1.88679) calc(var(--sp-md2)*1) 0',
            display: 'flex',
            alignItems: 'center'
          }}>
          {translate('businessInformation')}
        </span>
        
        <div style={{ 
          height: "calc(var(--fs-xs)*0.13650)", 
          background: 'linear-gradient(90deg, rgba(229, 231, 235, 0) 0%, rgba(26, 90, 120, 0.6) 25%, rgba(26, 90, 120, 1) 50%, rgba(26, 90, 120, 0.6) 75%, rgba(229, 231, 235, 0) 100%)',
          borderRadius: 'calc(var(--sp-md2)*0.5)',
          boxShadow: '0 4px 12px rgba(26, 90, 120, 0.15), 0 2px 6px rgba(26, 90, 120, 0.08)',
          marginLeft: 'calc(var(--sp-md2)*1)'
        }}/>
      </div>
      
      {/* Logo Card - Top Section */}
      <div
        style={{
          
          borderRadius: 'calc(var(--sp-md2)*1)',
          display: 'grid',
          gridTemplateColumns: '180px 1fr',
          gap: 'calc(var(--sp-md2)*2)',
          alignItems: 'center',
          marginBottom: 'calc(var(--sp-md2)*2.5)'
        }}>
        {/* Logo Circle */}
        <div
          style={{
            width: 'calc(var(--fs-hero)*3)',
            height: 'calc(var(--fs-hero)*3)',
            borderRadius: '50%',
            backgroundColor: '#fff',
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: `inset 0 1px 1px rgba(255,255,255,0.9), inset 0 -0.5px 1px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.02),
                                        0 0 0 2.5px rgba(0,0,0,0.03), 0 2px 7px rgba(0,0,0,0.05), 0 8px 22px rgba(0,0,0,0.06)`,
            padding: "calc(var(--sp-md2)*0.8490566)",
            position: "relative",
            cursor: "pointer",
            transition: 'all 200ms ease',
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
          onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
          onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}>
          {stateObj.previewDealerImage || stateObj.dealerAvatar ? (
            <ImageZoom
              imageSrc={
                stateObj.previewDealerImage && stateObj.dealerImageFile
                  ? stateObj.previewDealerImage
                  : stateObj.dealerAvatar
              }
              altSrc={
                stateObj.previewDealerImage && stateObj.dealerImageFile
                  ? stateObj.previewDealerImage
                  : stateObj.dealerAvatar
              }>
              <img
                src={
                  stateObj.previewDealerImage && stateObj.dealerImageFile
                    ? URL.createObjectURL(stateObj.previewDealerImage)
                    : stateObj.dealerAvatar
                }
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  outline: '1px solid transparent',
                  outlineOffset: -1
                }}
              />
            </ImageZoom>
          ) : (
            <ImageZoom imageSrc={DefaultAvatar} altSrc={DefaultAvatar}>
              <img
                src={DefaultAvatar}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  outline: '1px solid transparent',
                  outlineOffset: -1
                }}
              />
            </ImageZoom>
          )}

          {!isTeamMember && (
              <div
                onClick={() => document.getElementById('profile-upload')?.click()}
                role="button"
                style={{
                  position: 'absolute',
                  right: 'calc(var(--fs-xs)*-0.5)',
                  bottom: 'calc(var(--fs-xs)*0.5)',
                  width: 'calc(var(--fs-xl)*1.4150943)',
                  height: 'calc(var(--fs-xl)*1.4150943)',
                  borderRadius: '50%',
                  boxShadow: `
                    0 8px 24px rgba(59, 130, 246, 0.25),
                    0 4px 12px rgba(59, 130, 246, 0.15)
                `,
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 1000,
                  transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '2px solid #FFFFFF'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                  e.currentTarget.style.boxShadow = `
                        0 12px 32px rgba(59, 130, 246, 0.35),
                        0 6px 16px rgba(59, 130, 246, 0.25)
                    `;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = `
                        0 8px 24px rgba(59, 130, 246, 0.25),
                        0 4px 12px rgba(59, 130, 246, 0.15)
                    `;
                }}>
                <SVGImageComponentReact.IconEditPen
                  style={{
                    width: 'calc(var(--fs-xs)*1.4150943)',
                    height: 'calc(var(--fs-xs)*1.4150943)',
                    display: 'block',
                    opacity: 1,
                    filter: 'brightness(0) invert(1)'
                  }}
                />
              </div>
            )}
          <input
            id="profile-upload"
            type="file"
            onChange={onFileUpload}
            style={{ display: 'none' }}
            accept="image/*"
          />
        </div>
      </div>

      {/* Dealer Description */}
      <div style={{ gridColumn: '1 / -1' }}>
        <TextBase
          size={'calc(var(--fs-xs)*1.50943)'}
          fontWeight={600}
          color="#667085"
          style={{
            display: 'block',
            letterSpacing: '0.3px',
            marginBottom: 'calc(var(--sp-sm)*0.75)'
          }}>
          dealerDescription
        </TextBase>
        <FormArea
          disabled={isTeamMember}
          value={stateObj.description}
          placeholder="Professional automotive solutions"
          className="width-100"
          style={{
            width: '100%',
            fontSize: 'var(--fs-md2)',
            paddingLeft: 'calc(var(--sp-md2)*1.2)',
            paddingTop: 'calc(var(--sp-md2)*1.50943)',
            color: Colors.black,
            backgroundColor: '#FFF',
            border: '1px solid #E5E7EB',
            borderRadius: 'calc(var(--sp-md2)*0.5)',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            transition: 'all 0.2s ease',
            lineHeight:1.2,
            marginTop: 'calc(var(--sp-md2)*0.5)'
          }}
          onChange={(val) => setStateObj((prev) => ({ ...prev, description: val }))}
        />
      </div>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col xxl={12} span={24} style={{ marginTop: 'calc(var(--sp-md2)*1.88679)' }}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              className="ellipsis"
              size={'calc(var(--fs-xs)*1.50943)'}>
              businessEmail
            </TextBase>
            <LoginInput
              disabled={isTeamMember}
              value={stateObj.businessEmail}
              onChange={(val) => setStateObj((prev) => ({ ...prev, businessEmail: val }))}
              placeholder="enterEmail"
              noPaddingRight={true}
              style={{
                borderRadius: 'calc(var(--sp-md2)*0.75)',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
              }}
            />
          </div>
        </Col>

        <Col xxl={12} span={24} style={{ marginTop: 'calc(var(--sp-md2)*1.88679)' }}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              className="ellipsis"
              size={'calc(var(--fs-xs)*1.50943)'}>
              mobileNumber
            </TextBase>
            <div style={{ display: 'flex' }}>
              <div style={{ minWidth: 'calc(var(--fs-hero)*3.533773', zIndex: 2200 }}>
                <SelectCustom
                  className="full-width"
                  value={stateObj.phoneCode}
                  placeholder="Select PhoneCode"
                  searchPlaceholder={translate('searchCountryPhoneCode')}
                  items={renderCountriesPhoneCode()}
                  onSelect={(val) => onPhoneCodeSelect('phoneCode', val)}
                  showSearch
                  width="calc(var(--fs-hero)*8.13679)"
                />
              </div>
              <div style={{ display: 'flex', width: '100%' }}>
                <div style={{ width: '100%', marginLeft: 'var(--sp-md2)' }}>
                  <LoginInput
                    disabled={isTeamMember}
                    value={stateObj.phoneNumber}
                    onChange={(val) => setStateObj((prev) => ({ ...prev, phoneNumber: val }))}
                    placeholder="enterMobile"
                    noPaddingRight={true}
                    rightElement={
                      <ButtonGradient
                        disabled={
                          stateObj.phoneVerified &&
                          stateObj.phoneNumber === stateObj.phoneNumberPrev
                        }
                        className="verified-button"
                        onClick={() => {
                          return isTeamMember ? null : getSMSCode();
                        }}
                        textStyle={{ fontWeight: 600, fontSize: 'calc(var(--fs-xs)*1.60377)' }}
                        title={
                          stateObj.phoneVerified &&
                          stateObj.phoneNumber === stateObj.phoneNumberPrev
                            ? 'verified'
                            : 'verify'
                        }
                        backgroundColor={
                          stateObj.phoneVerified &&
                          stateObj.phoneNumber === stateObj.phoneNumberPrev
                            ? '#10B981'
                            : stateObj.phoneNumber !== stateObj.phoneNumberPrev
                            ? '#C8C8C8'
                            : null
                        }
                        style={{
                          padding: 'calc(var(--sp-md2)*1.88679)',
                          height: 'calc(var(--fs-hero)*1.179245)',
                          width: '100%',
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          boxShadow: '-2px 0px 2px 0px #4040400D'
                        }}
                        loading={loadingUpdateData}
                      />
                    }
                  />
                </div>
                <Popover
                  styles={{
                    body: {
                      fontSize: 'calc(var(--fs-xs)*1.32075)',
                      lineHeight: 'calc(var(--sp-md2)*1.88679)'
                    }
                  }}
                  content="Update phone to verify"
                  visible={stateObj.showOTPPopOver}
                  placement="topRight"></Popover>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="row-margin">
        <Col span={12}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              className="ellipsis"
              size={'calc(var(--fs-xs)*1.50943)'}>
              businessName
            </TextBase>
            <LoginInput
              disabled={isTeamMember}
              value={stateObj.businessName}
              onChange={(val) => setStateObj((prev) => ({ ...prev, businessName: val }))}
              placeholder="enterBusinessName"
              noPaddingRight={true}
              style={{
                borderRadius: 'calc(var(--sp-md2)*0.75)',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
              }}
            />
          </div>
        </Col>

        <Col span={12}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              size={'calc(var(--fs-xs)*1.50943)'}>
              taxPercentage
            </TextBase>
            <LoginInput
              disabled={isTeamMember}
              value={stateObj.taxPercentage}
              onChange={(val) => setStateObj((prev) => ({ ...prev, taxPercentage: val }))}
              placeholder="enterTaxPercentage"
              style={{
                borderRadius: 'calc(var(--sp-md2)*0.75)',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
              }}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="row-margin">
        <Col span={12}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              size={'calc(var(--fs-xs)*1.50943)'}>
              headquarterCountry
            </TextBase>
            <SelectCustom
              placeholder="Select Country"
              searchPlaceholder={translate('searchCountry')}
              disabled={isTeamMember}
              className="full-width"
              value={stateObj.country}
              items={renderCountries()}
              onSelect={onCountrySelect}
              showSearch
              style={{
                borderRadius: 'calc(var(--sp-md2)*0.75)',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
              }}
            />
          </div>
        </Col>

        {(stateObj.country === 'US' || stateObj.country === 'CA') && (
          <Col span={12}>
            <div>
              <TextBase
                style={{
                  textAlign: 'left',
                  display: 'block',
                  fontWeight: 600,
                  color: '#667085',
                  letterSpacing: '0.3px'
                }}
                size={'calc(var(--fs-xs)*1.50943)'}>
                state
              </TextBase>
              <SelectCustom
                placeholder="Select State"
                searchPlaceholder={translate('searchState')}
                disabled={isTeamMember}
                value={stateObj.stateVal}
                items={renderStates(stateObj.country)}
                onSelect={onStateSelect}
                showSearch
                style={{
                  borderRadius: 'calc(var(--sp-md2)*0.75)',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
                }}
              />
            </div>
          </Col>
        )}
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="row-margin">
        <Col span={24}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              size={'calc(var(--fs-xs)*1.50943)'}>
              address
            </TextBase>
            <LoginInput
              disabled={isTeamMember}
              value={stateObj.address}
              onChange={(val) => setStateObj((prev) => ({ ...prev, address: val }))}
              placeholder="enterAddress"
              style={{
                borderRadius: 'calc(var(--sp-md2)*0.75)',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
              }}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="row-margin">
        <Col span={12}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              size={'calc(var(--fs-xs)*1.50943)'}>
              city
            </TextBase>
            <LoginInput
              disabled={isTeamMember}
              value={stateObj.city}
              onChange={(val) => setStateObj((prev) => ({ ...prev, city: val }))}
              placeholder="enterCity"
              style={{
                borderRadius: 'calc(var(--sp-md2)*0.75)',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
              }}
            />
          </div>
        </Col>

        <Col span={12}>
          <div>
            <TextBase
              style={{
                textAlign: 'left',
                display: 'block',
                fontWeight: 600,
                color: '#667085',
                letterSpacing: '0.3px'
              }}
              size={'calc(var(--fs-xs)*1.50943)'}>
              zip
            </TextBase>
            <LoginInput
              disabled={isTeamMember}
              value={stateObj.zip}
              onChange={(val) => setStateObj((prev) => ({ ...prev, zip: val }))}
              placeholder="enterZip"
              style={{
                borderRadius: 'calc(var(--sp-md2)*0.75)',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
              }}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={30} style={{ marginTop: 'calc(var(--sp-md2)*1.88679)' }}>
        <Col span={12}>
          <div
            className="flex-row-center-center cursor-pointer cancel-button"
            style={{
              marginTop: 'calc(var(--sp-lg)*2.83018)',
              height: 'calc(var(--fs-hero)*1.32075)'
            }}>
            <TextBase
              onClick={handleCancel}
              size={'calc(var(--fs-xs)*1.50943)'}
              fontWeight={700}
              style={{ textAlign: 'center' }}
              color={Colors.grey}>
              cancel
            </TextBase>
          </div>
        </Col>
        <Col span={12}>
          <div className="flex-row-center-all" style={{ marginTop: 'calc(var(--sp-lg)*2.83018)' }}>
            <ButtonGradient
              onClick={saveVendor}
              textStyle={{ fontWeight: 700, fontSize: 'calc(var(--fs-xs)*1.50943)' }}
              title="update"
              style={{ width: '100%' }}
              loading={loadingUpdateData}
            />
          </div>
        </Col>
      </Row>

      <Modal open={isEditorOpen} onClose={handleCloseEditor}>
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <div
            style={{
              width: '60%',
              minHeight: 'calc(var(--fs-hero)*9.433962)',
              position: 'relative',
              background: '#000',
              padding: 'var(--sp-md2)'
            }}>
            <ImageEditor
              img={logoImage}
              setCroppedImage={handleCroppedImage2}
              handleCloseEditor={handleCloseEditor}
            />
          </div>
        </div>
      </Modal>
      <OTPModal
        type={'dealer'}
        fetchData={async () => {
          try {
            const id = getUserId(userInfo);
            if (!id) return;
            const resVendor = await APIManager.getInstance().getVendorProfile(id);
            const vendorData = resVendor?.data || {};
            setStateObj((prev) => ({
              ...prev,
              phoneVerified: !!vendorData?.phone_verified,
              phoneNumber: vendorData?.phone_number?.national_number || prev.phoneNumber,
              phoneCode: vendorData?.phone_number?.country_code || prev.phoneCode,
              phoneNumberPrev: vendorData?.phone_number?.national_number || prev.phoneNumberPrev
            }));
          } catch (e) {
            console.error('OTP fetchData', e);
          }
        }}
        getSMSCode={getSMSCode}
        showOTP={stateObj.showOTP}
        phone={stateObj.phoneNumber}
        phoneCode={stateObj.phoneCode}
        closeModal={closeModal}
      />
      {loadingUpdateData && <CustomSpinner fullPage />}
    </div>
  );
};

export default BusinessInformation;
